 import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FiCalendar, FiUploadCloud, FiFileText,
  FiChevronDown, FiX, FiExternalLink,
  FiDollarSign, FiTrendingUp,
} from "react-icons/fi";

const VRL   = "#5f56f8";
const VRL2  = "#4740d4";
const VRLL  = "#ededfe";
const VRLLB = "#c7c5fd";
const RED   = "#e5484d";
const RED2  = "#c53a3e";

/* ── Tools registry ── */
const TOOLS = [
  {
    key: "agenda",
    Icon: FiCalendar,
    title: "Agendas e Técnicos",
    sub: "Marcação de reuniões e visitas técnicas",
    url: "https://reuniao.eduall.io/",
  },
  {
    key: "import",
    Icon: FiUploadCloud,
    title: "Importação de Dados",
    sub: "Carregar e migrar dados para o EDUALL",
    url: "https://eduallapptechimports.kiossocamuegi.workers.dev",
  },
  {
    key: "docs",
    Icon: FiFileText,
    title: "Gestão de Documentos",
    sub: "Criação de contratos, cartas e propostas",
    url: "https://vrl-contratos-p-i31p.vercel.app/",
  },
  {
    key: "faturas",
    Icon: FiDollarSign,
    title: "Faturas Proforma",
    sub: "Criação de faturas proforma",
    url: "https://ftpm.eduall.site/",
  },
  {
    key: "roi",
    Icon: FiTrendingUp,
    title: "Calculadora de ROI",
    sub: "Simular a poupança e o retorno do EDUALL",
    url: "https://roi.eduall.site/",
  },
];

const S = {
  wrap:     { position: "relative", display: "inline-block", fontFamily: "'Montserrat',system-ui,sans-serif" },
  trigger:  {
    background: `linear-gradient(135deg,${VRL},${VRL2})`, color: "#fff", border: "none",
    fontWeight: 700, borderRadius: "999px", padding: "6px 22px", fontSize: ".9rem",
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "10px",
    fontFamily: "inherit", boxShadow: "0 4px 16px rgba(95,86,248,.35)",
  },
  chevron:  (open) => ({ transition: "transform .18s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)" }),
  menu:     {
    position: "absolute", top: "calc(100% + 10px)", left: 0, minWidth: "300px",
    background: "#fff", borderRadius: "16px", border: "1px solid #ececf5",
    boxShadow: "0 20px 50px rgba(30,20,90,.18)", overflow: "hidden", zIndex: 1000,
    animation: "toolsMenuIn .16s ease",
  },
  menuLabel: {
    fontSize: ".68rem", fontWeight: 800, letterSpacing: "2px", textTransform: "uppercase",
    color: "#9691c9", padding: "14px 18px 8px",
  },
  item:     {
    display: "flex", alignItems: "flex-start", gap: "12px", padding: "12px 18px",
    cursor: "pointer", textDecoration: "none", color: "inherit", background: "transparent",
    transition: "background .12s ease", border: "none", width: "100%", textAlign: "left",
  },
  itemIcon: {
    width: "36px", height: "36px", borderRadius: "10px", background: VRLL, color: VRL,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "1px",
  },
  itemTitle: { fontSize: ".87rem", fontWeight: 700, color: "#1a1a1a" },
  itemSub:  { fontSize: ".74rem", color: "#7a7a8c", marginTop: "2px", lineHeight: 1.4 },
  extIcon:  { marginLeft: "auto", color: "#c7c5da", flexShrink: 0, marginTop: "8px" },

  overlay: {
    position: "fixed", inset: 0, width: "100vw", height: "100dvh",
    background: "#0b0b14", zIndex: 99999, display: "flex",
  },
  iframe: { width: "100%", height: "100%", border: "none", display: "block" },
  closeBtn: {
    position: "fixed", top: "16px", right: "16px", zIndex: 100000,
    width: "44px", height: "44px", borderRadius: "50%",
    background: RED, color: "#fff", border: "none", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 6px 18px rgba(197,58,62,.45)", transition: "background .15s, transform .1s",
  },
  loading: {
    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: "14px",
    color: "#c9c7e8", background: "#0b0b14",
  },
  spinner: {
    width: "36px", height: "36px", borderRadius: "50%",
    border: "3px solid rgba(255,255,255,.15)", borderTopColor: VRLLB,
    animation: "toolsSpin .8s linear infinite",
  },
};

export default function ToolsMenu() {
  const [open, setOpen]           = useState(false);
  const [activeTool, setActive]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc   = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setActive((cur) => (cur ? null : cur));
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = activeTool ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [activeTool]);

  const openTool = useCallback((tool) => {
    setOpen(false);
    setLoading(true);
    setActive(tool);
  }, []);

  const closeTool = useCallback(() => {
    setActive(null);
    setLoading(false);
  }, []);

  return (
    <div style={S.wrap} ref={wrapRef}>
      <button style={S.trigger} onClick={() => setOpen((o) => !o)}>
        Ferramentas EDUALL
        <FiChevronDown size={16} style={S.chevron(open)} />
      </button>

      {open && (
        <div style={S.menu}>
          <div style={S.menuLabel}>Aceder a</div>
          {TOOLS.map((t) => (
            <button
              key={t.key}
              style={S.item}
              onClick={() => openTool(t)}
              onMouseEnter={(e) => (e.currentTarget.style.background = VRLL)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div style={S.itemIcon}><t.Icon size={16} /></div>
              <div>
                <div style={S.itemTitle}>{t.title}</div>
                <div style={S.itemSub}>{t.sub}</div>
              </div>
              <FiExternalLink size={13} style={S.extIcon} />
            </button>
          ))}
        </div>
      )}

      {activeTool && (
        <div style={S.overlay}>
          {loading && (
            <div style={S.loading}>
              <div style={S.spinner} />
              <div style={{ fontSize: ".85rem", fontWeight: 600 }}>
                A abrir {activeTool.title}…
              </div>
            </div>
          )}
          <iframe
            title={activeTool.title}
            src={activeTool.url}
            style={{ ...S.iframe, opacity: loading ? 0 : 1 }}
            onLoad={() => setLoading(false)}
            allow="clipboard-read; clipboard-write; fullscreen"
          />
          <button
            style={S.closeBtn}
            onClick={closeTool}
            aria-label="Fechar"
            onMouseEnter={(e) => (e.currentTarget.style.background = RED2)}
            onMouseLeave={(e) => (e.currentTarget.style.background = RED)}
            onMouseDown={(e) => (e.currentTarget.style.transform = "scale(.92)")}
            onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <FiX size={20} />
          </button>
        </div>
      )}

      <style>{`
        @keyframes toolsMenuIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
        @keyframes toolsSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}